import React from 'react';
import HourlyWeatherGrid from './HourlyWeatherGrid';
import WeatherHistory from './WeatherHistory';
import FishingForecast from './FishingForecast';
import { AIRecommendation } from './AIRecommendation';
import { WindSimulator } from './WindSimulator';
import { HourlyWeather, DailyWeather, FishingScore, Coordinates } from '../models/WeatherData';
import { StorageService } from '../services/StorageService';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
  convertTemperature, 
  convertWindSpeed, 
  convertPressure,
  convertPrecipitation,
  getTemperatureUnit,
  getWindSpeedUnit,
  getPressureUnit,
  getPrecipitationUnit
} from '../utils/unitConversion';

interface WeatherDisplayProps {
  locationName: string;
  currentWeather: HourlyWeather | null;
  hourlyWeather: HourlyWeather[];
  dailyWeather: DailyWeather[];
  fishingScores: FishingScore[];
  dailyFishingScores: FishingScore[];
  selectedDate: string;
  coordinates: Coordinates | null;
  species: string; // Species key (e.g., 'pike', 'perch', etc.)
  onAddFavorite?: () => void;
}

const WeatherDisplay: React.FC<WeatherDisplayProps> = ({
  locationName,
  currentWeather,
  hourlyWeather,
  dailyWeather,
  fishingScores,
  dailyFishingScores,
  selectedDate,
  coordinates,
  species,
  onAddFavorite,
}) => {
  const { t } = useLanguage();
  const { unitSystem } = useSettings();
  
  // Check if current location is a favorite
  const isFavorite = coordinates ? StorageService.getFavorites().some(
    fav => Math.abs(fav.lat - coordinates.lat) < 0.01 && Math.abs(fav.lng - coordinates.lng) < 0.01
  ) : false;
  // Helper functions
  const getWeatherDescription = (code: number): string => {
    const descriptions: { [key: number]: string } = {
      0: t.weatherClear,
      1: t.weatherMostlyClear,
      2: t.weatherPartlyCloudy,
      3: t.weatherCloudy,
      45: t.weatherFog,
      48: t.weatherRimeFog,
      51: t.weatherLightDrizzle,
      53: t.weatherModerateDrizzle,
      55: t.weatherDenseDrizzle,
      61: t.weatherLightRain,
      63: t.weatherModerateRain,
      65: t.weatherHeavyRain,
      71: t.weatherLightSnow,
      73: t.weatherModerateSnow,
      75: t.weatherHeavySnow,
      80: t.weatherLightShowers,
      81: t.weatherModerateShowers,
      82: t.weatherHeavyShowers,
      95: t.weatherThunderstorm,
      96: t.weatherThunderstormHail
    };
    return descriptions[code] || t.weatherUnknown;
  };

  const getWeatherIcon = (code: number): string => {
    if (code === 0) return '☀️';
    if (code <= 2) return '🌤️';
    if (code === 3) return '☁️';
    if (code >= 45 && code <= 48) return '🌫️';
    if (code >= 51 && code <= 55) return '🌦️';
    if (code >= 61 && code <= 65) return '🌧️';
    if (code >= 71 && code <= 75) return '❄️';
    if (code >= 80 && code <= 82) return '🌧️';
    if (code >= 95) return '⛈️';
    return '🌤️';
  };

  const getWindDirectionText = (deg: number): string => {
    // Simplified 8-direction compass
    const directions = [t.north, t.northeast, t.east, t.southeast, t.south, t.southwest, t.west, t.northwest];
    const index = Math.round(deg / 45) % 8;
    return directions[index];
  };

  const getWindDirectionArrow = (deg: number): string => {
    // Wind direction indicates where wind comes FROM
    // Add 180° to show where it's blowing TO
    const direction = (deg + 180) % 360;
    
    // Return arrow based on direction (8 directions)
    if (direction >= 337.5 || direction < 22.5) return '↑';    // N
    if (direction >= 22.5 && direction < 67.5) return '↗';     // NE
    if (direction >= 67.5 && direction < 112.5) return '→';    // E
    if (direction >= 112.5 && direction < 157.5) return '↘';   // SE
    if (direction >= 157.5 && direction < 202.5) return '↓';   // S
    if (direction >= 202.5 && direction < 247.5) return '↙';   // SW
    if (direction >= 247.5 && direction < 292.5) return '←';   // W
    if (direction >= 292.5 && direction < 337.5) return '↖';   // NW
    return '↓';
  };

  const getMoonPhaseText = (phase: number): string => {
    if (phase >= 0 && phase < 0.03) return `🌑 ${t.newMoon}`;
    if (phase >= 0.03 && phase < 0.22) return `🌒 ${t.waxingCrescent}`;
    if (phase >= 0.22 && phase < 0.28) return `🌓 ${t.firstQuarter}`;
    if (phase >= 0.28 && phase < 0.47) return `🌔 ${t.waxingGibbous}`;
    if (phase >= 0.47 && phase < 0.53) return `🌕 ${t.fullMoon}`;
    if (phase >= 0.53 && phase < 0.72) return `🌖 ${t.waningGibbous}`;
    if (phase >= 0.72 && phase < 0.78) return `🌗 ${t.lastQuarter}`;
    if (phase >= 0.78 && phase < 0.97) return `🌘 ${t.waningCrescent}`;
    if (phase >= 0.97 && phase <= 1.0) return `🌑 ${t.newMoon}`;
    return `🌑 ${t.newMoon}`;
  };

  const getMoonPhaseEmoji = (phase: number): string => {
    if (phase >= 0 && phase < 0.03) return '🌑';
    if (phase >= 0.03 && phase < 0.22) return '🌒';
    if (phase >= 0.22 && phase < 0.28) return '🌓';
    if (phase >= 0.28 && phase < 0.47) return '🌔';
    if (phase >= 0.47 && phase < 0.53) return '🌕';
    if (phase >= 0.53 && phase < 0.72) return '🌖';
    if (phase >= 0.72 && phase < 0.78) return '🌗';
    if (phase >= 0.78 && phase < 0.97) return '🌘';
    if (phase >= 0.97 && phase <= 1.0) return '🌑';
    return '🌑';
  };

  const formatTime = (timeStr: string): string => {
    if (!timeStr) return '';
    try {
      const date = new Date(timeStr);
      return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return timeStr;
    }
  };

  if (!currentWeather) {
    return (
      <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-6 sm:p-8">
        <div className="h-full flex flex-col items-center justify-center py-16">
          <div className="text-6xl mb-4">🌍</div>
          <h3 className="text-lg font-semibold text-white mb-2">{t.welcomeTitle}</h3>
          <p className="text-slate-400 text-sm text-center max-w-sm">
            {t.welcomeSubtitle}
          </p>
        </div>
      </div>
    );
  }

  // Determine weather type badge
  const today = new Date().toISOString().split('T')[0];
  const selectedDateObj = new Date(selectedDate);
  const todayObj = new Date(today);
  const diffDays = Math.floor((selectedDateObj.getTime() - todayObj.getTime()) / (1000 * 60 * 60 * 24));
  
  let typeBadge = '';
  let badgeColor = '';
  if (diffDays < 0) {
    typeBadge = t.historicalWeather;
    badgeColor = 'from-purple-500/20 to-pink-500/20 text-purple-300';
  } else if (diffDays > 0) {
    typeBadge = t.forecast;
    badgeColor = 'from-blue-500/20 to-cyan-500/20 text-blue-300';
  } else {
    typeBadge = t.currentWeather;
    badgeColor = 'from-green-500/20 to-emerald-500/20 text-green-300';
  }

  // Format date
  const formattedDate = new Date(selectedDate).toLocaleDateString('sv-SE', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const weatherIcon = getWeatherIcon(currentWeather.weather_code || 0);
  const weatherDesc = getWeatherDescription(currentWeather.weather_code || 0);

  // Calculate all overview values from hourly data to ensure consistency
  const maxWindSpeed = hourlyWeather.length > 0 ? Math.max(...hourlyWeather.map(h => h.windSpeed || 0)) : currentWeather.windSpeed;
  const maxWindGust = hourlyWeather.length > 0 ? Math.max(...hourlyWeather.map(h => h.windGust || 0)) : currentWeather.windGust;
  const totalPrecipitation = hourlyWeather.reduce((sum, hour) => sum + (hour.precipitation || 0), 0);
  const maxTemp = hourlyWeather.length > 0 ? Math.max(...hourlyWeather.map(h => h.temperature || 0)) : currentWeather.temperature;
  const minTemp = hourlyWeather.length > 0 ? Math.min(...hourlyWeather.map(h => h.temperature || 0)) : currentWeather.temperature;

  return (
    <>
      {/* Main Weather Info */}
      <div id="weather-info" className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] shadow-lg p-4 sm:p-6">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${badgeColor} border border-white/10`}>
                {typeBadge}
              </span>
              {isFavorite && (
                <span className="text-lg text-yellow-400 p-1" title="Favorit">
                  ⭐
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">{locationName}</h2>
            </div>
            <p className="text-sm text-slate-400">{formattedDate}</p>
          </div>
          
          {/* Main weather display */}
          <div className="flex-1 flex flex-col justify-center">
            <div className="text-center mb-8">
              <div className="text-6xl mb-4">{weatherIcon}</div>
              <div className="text-4xl font-bold text-white mb-2">
                {Math.round(convertTemperature(currentWeather.temperature, unitSystem))}{getTemperatureUnit(unitSystem)}
              </div>
              {hourlyWeather.length > 0 && (
                <div className="text-sm text-slate-400 mb-1">
                  Min: {Math.round(convertTemperature(minTemp, unitSystem))}{getTemperatureUnit(unitSystem)} • Max: {Math.round(convertTemperature(maxTemp, unitSystem))}{getTemperatureUnit(unitSystem)} 
                </div>
              )}
              <div className="text-slate-300 text-lg capitalize">{weatherDesc}</div>
            </div>
            
            {/* Weather metrics */}
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.humidity}</div>
                <div className="text-xl font-semibold text-white">{currentWeather.humidity}%</div>
              </div>
              <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.wind}</div>
                {/* Compact format: Direction Arrow Speed(Gust) */}
                <div className="text-xl font-semibold text-white">
                  {currentWeather.windDirection !== undefined && (
                    <span className="mr-1">{getWindDirectionText(currentWeather.windDirection)} {getWindDirectionArrow(currentWeather.windDirection)}</span>
                  )}
                  {Math.round(convertWindSpeed(maxWindSpeed, unitSystem))}
                  {(maxWindGust || 0) > maxWindSpeed && (
                    <span>({Math.round(convertWindSpeed(maxWindGust || 0, unitSystem))})</span>
                  )} <span className="text-base">{getWindSpeedUnit(unitSystem)}</span>
                </div>
              </div>
              <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.pressure}</div>
                <div className="text-xl font-semibold text-white">
                  {unitSystem === 'imperial' 
                    ? convertPressure(currentWeather.pressure, unitSystem).toFixed(2)
                    : Math.round(convertPressure(currentWeather.pressure, unitSystem))
                  } {getPressureUnit(unitSystem)}
                </div>
              </div>
              <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">{t.precipitation}</div>
                <div className="text-xl font-semibold text-white">
                  {unitSystem === 'imperial' 
                    ? convertPrecipitation(totalPrecipitation, unitSystem).toFixed(2)
                    : totalPrecipitation.toFixed(1)
                  } {getPrecipitationUnit(unitSystem)}
                </div>
              </div>
            </div>
            
            {/* Astronomisk information */}
            {(currentWeather.sunrise || currentWeather.sunset || currentWeather.moonPhase !== undefined) && (
              <div className="mt-6 pt-6 border-t border-white/[0.08]">
                <h3 className="text-sm font-medium text-slate-400 mb-3 uppercase tracking-wider">{t.sunAndMoon}</h3>
                <div className="grid grid-cols-2 gap-4">
                  {currentWeather.sunrise && (
                    <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                      <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">🌅 {t.sunrise}</div>
                      <div className="text-xl font-semibold text-white">{formatTime(currentWeather.sunrise)}</div>
                    </div>
                  )}
                  {currentWeather.sunset && (
                    <div className="rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08]">
                      <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">🌇 {t.sunset}</div>
                      <div className="text-xl font-semibold text-white">{formatTime(currentWeather.sunset)}</div>
                    </div>
                  )}
                  {currentWeather.moonPhase !== undefined && (
                    <div className={`rounded-2xl bg-white/[0.03] p-4 border border-white/[0.08] ${currentWeather.sunrise && currentWeather.sunset ? 'col-span-2' : ''}`}>
                      <div className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1">🌙 {t.moonPhase}</div>
                      <div className="text-xl font-semibold text-white">
                        {getMoonPhaseEmoji(currentWeather.moonPhase)} {getMoonPhaseText(currentWeather.moonPhase).replace(/🌑|🌒|🌓|🌔|🌕|🌖|🌗|🌘/g, '').trim()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hourly Weather */}
      <div data-section="hourly">
        <HourlyWeatherGrid hourlyWeather={hourlyWeather} />
      </div>

      {/* Weather History */}
      <div data-section="history">
        <WeatherHistory dailyWeather={dailyWeather} selectedDate={selectedDate} />
      </div>

      {/* Fishing Forecast */}
      <div data-section="fishing">
        <FishingForecast
          fishingScores={fishingScores}
          dailyFishingScores={dailyFishingScores}
          dailyWeather={dailyWeather}
          selectedDate={selectedDate}
        />
      </div>

      {/* Wind Simulator */}
      <div data-section="wind">
        {hourlyWeather.length > 0 && coordinates && (
          <WindSimulator
            hourlyWeather={hourlyWeather}
            selectedDate={selectedDate}
            coordinates={coordinates}
          />
        )}
      </div>

      {/* AI Recommendation - Moved to bottom */}
      <div data-section="ai">
        {currentWeather && hourlyWeather.length > 0 && dailyWeather.length > 0 && coordinates && (
          <AIRecommendation
            coordinates={coordinates}
            currentWeather={currentWeather}
            hourlyWeather={hourlyWeather}
            dailyWeather={dailyWeather}
            fishingScore={fishingScores[0] || { 
              score: 50, 
              factors: [], 
              criticalFactors: [],
              debug: {
                sLight: '', sPress: '', sWind: '', sPC: '', sMoon: '', sSea: '', sStab: '',
                frontMul: '', waterTemp: '', trend6h: '', trend24h: '', hourIndex: 0, cloudiness: ''
              }
            }}
            selectedDate={selectedDate}
            species={species}
          />
        )}
      </div>
    </>
  );
};

export default WeatherDisplay;
